import {getBookingFinalizationLink} from 'business/bofhLinks'
import {logToDataDog} from 'services/datadogLogger'
import {
  BaseInitializationResponse,
  BoFHResponses,
  ChallengeResponse,
  FinalizationBookingResponse,
  Psd2Strategy,
  ServerCode,
  StatusCodes
} from 'services/psd2/types'
import {stripe} from 'services/stripe'

import {
  BookingStatusThreeDomainSecureRequiredResponse,
  StripeThreeDomainSecureParams
} from '@vio/core/src/booking/types/BookingStatus'

import type {
  PaymentIntent,
  PaymentIntentOrSetupIntentResult,
  SetupIntent
} from '@stripe/stripe-js'

export type StripeBookingCreationResponse =
  BookingStatusThreeDomainSecureRequiredResponse<StripeThreeDomainSecureParams>

const _bofhResponses: BoFHResponses<StripeBookingCreationResponse> = {
  initialization: undefined,
  challenge: undefined,
  isChargeLater: false
}

const initializeBooking = async (
  resp: StripeBookingCreationResponse
): Promise<BaseInitializationResponse> => {
  if (resp) _bofhResponses.initialization = resp
  logToDataDog({
    logName: 'services.PSD2.Stripe.initializedBookingResponse',
    metadata: {
      meta: _bofhResponses.initialization
    }
  })
  return {
    statusCode: StatusCodes.SUCCESS
  }
}

const challenge = async (): Promise<ChallengeResponse> => {
  try {
    const isChargeLater = getIsChargeLater()
    let intent: PaymentIntent | SetupIntent | undefined
    let result: PaymentIntentOrSetupIntentResult
    if (isChargeLater) {
      result = await stripe.handleNextAction(getClientSecret())
      intent = result?.setupIntent
    } else {
      result = await stripe.handleCardAction(getClientSecret())
      intent = result?.paymentIntent
    }
    if (intent) {
      const challengeSucceded = didChallengeSucceed(intent)
      if (challengeSucceded) {
        return {
          status: StatusCodes.SUCCESS
        }
      }
    }

    return {
      status: StatusCodes.ERROR,
      error: result?.error
    }
  } catch (error) {
    logToDataDog({
      logName: 'services.PSD2.Stripe.ChallengeLoadError',
      metadata: {meta: error}
    })
    return {
      status: StatusCodes.ERROR,
      error
    }
  }
}

const finalizeBooking = async (): Promise<FinalizationBookingResponse> => {
  const params = _bofhResponses.initialization
  const bookingFinalizationLink = getBookingFinalizationLink(params?.links)

  logToDataDog({
    logName: 'services.PSD2.Stripe.finalizeBooking',
    metadata: {meta: {bookingFinalizationLink}}
  })

  return {
    success: true,
    link: bookingFinalizationLink
  }
}
// #endregion

const getClientSecret = () => {
  return (
    _bofhResponses?.initialization?.threeDomainSecure?.params?.clientSecret ||
    ''
  )
}
const getIsChargeLater = () => _bofhResponses.isChargeLater

const isEnrolled = (
  resp: StripeBookingCreationResponse,
  isChargeLater: boolean = false
) => {
  _bofhResponses.initialization = resp
  _bofhResponses.isChargeLater = isChargeLater
  return (
    resp?.status === 'three_domain_secure_required' ||
    (resp?.threeDomainSecure?.enrolled &&
      resp?.threeDomainSecure.serverCode === ServerCode.Stripe)
  )
}

const didChallengeSucceed = (paymentIntent: PaymentIntent | SetupIntent) => {
  if (!paymentIntent) return false
  return (
    paymentIntent.status === 'requires_capture' ||
    paymentIntent.status === 'succeeded' ||
    paymentIntent.status === 'requires_confirmation'
  )
}

const Psd2: Psd2Strategy<void, StripeBookingCreationResponse> = {
  setup: () => null, // stripe is initialized after payment-methods
  initializeBooking,
  finalizeBooking,
  challenge,
  isEnrolled,
  serverCode: ServerCode.Stripe
}

export default Psd2
